import React, { type FC, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Button, Select, TextArea, FileUpload } from "components/Shared/Inputs";
import { type UploadDocumentType, type UploadPayload } from 'api/types';
import { ALLOWED_DOCUMENT_TYPES } from './type'
import { useAuth } from 'components/Auth';
import { useUploadFile } from 'api/queries/fileUploadQuery';
import { type DocumentDescriptor } from 'components/ProviderProfile/interfaces';
import { ApiError } from 'api/error/ApiError';

import "./ActivityConsentFormUploader.scss";

interface ActivityConsentFormUploaderProps {
  activityId: number;
  onClose: (data : DocumentDescriptor | undefined) => void;
}

export const ActivityConsentFormUploader: FC<ActivityConsentFormUploaderProps> = ({ activityId, onClose }) => {
  const [documentType, setDocumentType] = useState<UploadDocumentType>('parental-consent-form');
  const [documentNotes, setDocumentNotes] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(false);
  const { isPending, mutateAsync: uploadFile } = useUploadFile({
    onSuccess: (response)=> handleSuccess(response as DocumentDescriptor),
    onError: (error, variables) => handleError(error, variables),
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['documentUpload', 'common', 'fileupload'], { useSuspense: false });
  const { effectiveProviderData } = useAuth();



  const handleDocumentTypeChange = (event) => {
    setDocumentType(event.target.value as UploadDocumentType);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDocumentNotes(event.target.value);
  };

  const handleVerificationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsVerified(event.target.checked);
  };

  const handleFileSelect = (file: File | null) => {
    setSaveError('');
    setIsVerified(false);
    setSelectedFile(file);
  };

  const handleSuccess = (response?: DocumentDescriptor) => {
    if(response) {
      enqueueSnackbar(`${t(response?.metadata?.document_type)} ${t('uploaded_successfully')}.`, { variant: 'success' });

      if(selectedFile) {
        onClose(response);
      }
    }
    else {
      onClose(undefined);
    }
  }

  const generateErrorMessage = (filename: string, errorMessage: string) => {
    return `${errorMessage} ${t('for')} "${filename}"`
  }

  const handleError = (error, variables: UploadPayload)=> {
    if (error instanceof ApiError && error.isBusinessError) {
      setSaveError(generateErrorMessage(variables.file.name, t(`fileupload:${error.code}`)));
    } else {
      setSaveError(generateErrorMessage(variables.file.name, error.message));
    }
  }
  
  const handleFileUpload = async (activityId: number, documentType: UploadDocumentType, file: File, notes: string) => {
      const providerId = effectiveProviderData?.provider?.providerId?.toString();
      const providerFirstName = effectiveProviderData?.provider?.firstName;
      const providerLastName = effectiveProviderData?.provider?.lastName;
      try {
        await uploadFile(
          {
            file,
            $type: 'activity',
            id: activityId,
            documentType,
            metadata: {
                notes : notes ? btoa(notes) : undefined,
                providerId,
                providerFirstName: providerFirstName ? btoa(providerFirstName) : '',
                providerLastName: providerLastName ? btoa(providerLastName) : ''
            }
        });
      } catch (error) {
        console.error('File upload failed:', error);
      }
  };

  const handleSave = async () => {
    if(selectedFile) {
      await handleFileUpload(activityId, documentType, selectedFile, documentNotes);
    } 
  };

  const handleCancel = () => {
    onClose(undefined);
  };

  const isSaveDisabled = !selectedFile || !documentType || !isVerified || fileError || !!saveError;
  const isCheckBoxDisabled = !selectedFile || !documentType || fileError || !!saveError;

  return (
    <Container className="activity_form_container">
    <Grid container component='form' spacing={3} className='activity_consent_form'>
    <Grid item xs={12}>
      <h3>{t('upload_document')}</h3>
      <span>{t('please_select_document_type_before_uploading_your_document')}.</span>
    </Grid>
    <Grid item xs={6}>
      <Select
        label={`${t('document_type')}*`}
        name="documentType"
        value={documentType}
        onChange={handleDocumentTypeChange}
        className="document_type_input" fullWidth
      >
        {ALLOWED_DOCUMENT_TYPES.map((docType) => (
          <MenuItem value={docType} key={docType}>{t(docType)}</MenuItem>))}
      </Select>
  </Grid>

  <Grid item xs={6}>
    <TextArea
      value={documentNotes}
      onChange={handleNotesChange}
      placeholder={t('add_notes')}
      maxLength={250}
    />
  </Grid>

        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12}>
            <FileUpload onFileSelect={handleFileSelect} onFileError={setFileError} uploadError={saveError} />
          </Grid>        
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isCheckBoxDisabled}
                  checked={isVerified}
                  onChange={handleVerificationChange}
                />
              }
              label={t('i_confirm_that_this_document_is_a_valid_copy')}
            />
          </Grid>

        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item>
            <Button
              busy = {isPending}
              busyText={t('saving')}
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {t('common:save')}
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant='text'
              onClick={handleCancel}
            >
              {t('common:cancel')}
            </Button>
          </Grid>
        </Grid>
    </Grid>
    </Container>
  );
};